import React, { useEffect, useMemo, useState } from "react";
import { X, Mail, Loader2, UserPlus, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * RoleBasedInviteModal
 *
 * Unified, hierarchical invitation dialog. The dropdown of invitable roles is
 * fetched live from `GET /api/auth/invitable-roles` so the same component
 * works for every authorised inviter:
 *   admin           → BCM Manager
 *   bcm_manager     → Department Head | IT-DRP Lead | (gov exceptions)
 *   department_head → Process Manager
 *   process_manager → End User (Process Contributor) | App Manager
 *   it_drp_manager  → IT Function Contributor
 *
 * When the chosen role declares `scope=department|process|organization`, the
 * modal also renders a scope picker so the invitee is auto-attached to the
 * correct operational unit on accept (department_id / process_id /
 * target_org_id flow through to `POST /api/auth/invite`).
 */
export default function RoleBasedInviteModal({ open, onClose, onInvited, defaultRole, defaultDepartmentId, defaultDepartmentName, defaultProcessId }) {
  const { api, user } = useAuth();
  const [matrix, setMatrix] = useState({ inviter_role: "", options: [] });
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const [form, setForm] = useState({
    role: "",
    email: "",
    first_name: "",
    last_name: "",
    department_id: "",
    department: "",
    process_id: "",
    target_org_id: "",
  });

  // Reset whenever the modal opens
  useEffect(() => {
    if (!open) return;
    setForm({
      role: defaultRole || "",
      email: "",
      first_name: "",
      last_name: "",
      department_id: defaultDepartmentId || "",
      department: defaultDepartmentName || "",
      process_id: defaultProcessId || "",
      target_org_id: "",
    });
  }, [open, defaultRole, defaultDepartmentId, defaultDepartmentName, defaultProcessId]);

  // Fetch the role matrix on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setMatrixLoading(true);
      try {
        const res = await api.get("/auth/invitable-roles");
        if (cancelled) return;
        setMatrix(res.data);
        // Auto-select first option to reduce clicks
        if (res.data?.options?.length === 1) {
          setForm((f) => ({ ...f, role: res.data.options[0].value }));
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || "You are not authorised to invite team members.");
        }
      } finally {
        if (!cancelled) setMatrixLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, api]);

  const selectedOption = useMemo(
    () => matrix.options.find((o) => o.value === form.role),
    [matrix.options, form.role],
  );
  const scope = selectedOption?.scope || null;

  // Lazy-load scope data only when the chosen role needs it.
  useEffect(() => {
    if (!open || !scope) return;
    let cancelled = false;
    (async () => {
      try {
        if (scope === "department" && departments.length === 0) {
          const res = await api.get("/departments");
          if (!cancelled) setDepartments(res.data || []);
        } else if (scope === "process" && processes.length === 0) {
          const res = await api.get("/processes");
          if (!cancelled) setProcesses(res.data || []);
        } else if (scope === "organization" && organizations.length === 0) {
          const res = await api.get("/admin/organizations?limit=200");
          if (!cancelled) setOrganizations(res.data || []);
        }
      } catch {
        // Scope endpoints can 403 for some inviters — silently fall back to
        // free-text department name input.
      }
    })();
    return () => { cancelled = true; };
  }, [open, scope, api, departments.length, processes.length, organizations.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.role) { toast.error("Please choose a role to invite."); return; }
    if (!form.email || !form.first_name) { toast.error("First name and email are required."); return; }
    if (scope === "department" && !form.department_id && !form.department) {
      toast.error("Please choose the department this invitee will own.");
      return;
    }
    if (scope === "process" && !form.process_id) {
      toast.error("Please choose the process this invitee will work on.");
      return;
    }
    if (scope === "organization" && matrix.inviter_role === "admin" && !form.target_org_id) {
      toast.error("Please choose the organization to seed.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name || "",
        role: form.role,
      };
      if (form.department_id) payload.department_id = form.department_id;
      if (form.department) payload.department = form.department;
      if (form.process_id) payload.process_id = form.process_id;
      if (form.target_org_id) payload.target_org_id = form.target_org_id;

      const res = await api.post("/auth/invite", payload);
      // V22.9 — Manual-mode awareness. When SendGrid is not configured
      // the backend returns delivery="manual" + accept_url so the
      // WebAdmin can hand-deliver the activation link. We never show
      // an "email failed" toast for this case.
      const delivery = res.data?.delivery;
      const acceptUrl = res.data?.accept_url;
      if (delivery === "manual" && acceptUrl) {
        const fullUrl = acceptUrl.startsWith("http")
          ? acceptUrl
          : `${window.location.origin}${acceptUrl}`;
        try { await navigator.clipboard.writeText(fullUrl); } catch { /* clipboard may be blocked */ }
        toast.success(
          `Success (Manual Mode) — invite for ${form.email} created. Activation link copied to clipboard.`,
          {
            description:
              "SendGrid email delivery is not configured yet. Paste the link into Slack DM, secure email, or phone to deliver to the recipient. The link is also printed in the backend console.",
            duration: 9000,
          }
        );
      } else if (delivery === "sent") {
        toast.success(`Invitation email sent to ${form.email} as ${selectedOption?.label}`);
      } else if (delivery === "email_failed") {
        const fullUrl = acceptUrl
          ? (acceptUrl.startsWith("http") ? acceptUrl : `${window.location.origin}${acceptUrl}`)
          : null;
        if (fullUrl) { try { await navigator.clipboard.writeText(fullUrl); } catch { /* clipboard may be blocked */ } }
        toast.warning(
          `Invitation created, but the email failed to send to ${form.email}.`,
          {
            description:
              (res.data?.delivery_note || "The recipient was not emailed.") +
              (fullUrl ? " The activation link has been copied to your clipboard — deliver it manually, or use Resend from the Invitations view." : " Use Resend from the Invitations view."),
            duration: 11000,
          }
        );
      } else {
        toast.success(`Invitation generated for ${form.email} as ${selectedOption?.label}`);
      }
      onInvited?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
      data-testid="role-invite-modal"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-sky-600" />
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Invite team member</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Inviting as <span className="font-medium text-slate-700">
                  {(matrix.inviter_role || user?.role || "").replace(/_/g, " ")}
                </span> · 7-day OTP via email
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            data-testid="role-invite-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Role selector */}
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Role <span className="text-rose-500">*</span>
            </label>
            {matrixLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading available roles…
              </div>
            ) : matrix.options.length === 0 ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Your current role cannot invite other team members.
              </p>
            ) : (
              <div className="relative">
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2 pr-9 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 appearance-none"
                  required
                  data-testid="role-invite-role-select"
                >
                  <option value="">— Choose role —</option>
                  {matrix.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            )}
            {selectedOption?.description && (
              <p className="mt-1 text-xs text-slate-500">{selectedOption.description}</p>
            )}
          </div>

          {/* Scope selector — department / process / organization */}
          {scope === "department" && (
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Department <span className="text-rose-500">*</span>
              </label>
              {departments.length > 0 ? (
                <select
                  value={form.department_id}
                  onChange={(e) => {
                    const d = departments.find((x) => x.id === e.target.value);
                    setForm({ ...form, department_id: e.target.value, department: d?.name || "" });
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  required
                  data-testid="role-invite-department-select"
                >
                  <option value="">— Choose department —</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  required
                  placeholder="Department name"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  data-testid="role-invite-department-input"
                />
              )}
            </div>
          )}

          {scope === "process" && (
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Process <span className="text-rose-500">*</span>
              </label>
              <select
                value={form.process_id}
                onChange={(e) => setForm({ ...form, process_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                required
                data-testid="role-invite-process-select"
              >
                <option value="">— Choose process —</option>
                {processes.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {processes.length === 0 && (
                <p className="mt-1 text-xs text-amber-700">
                  No processes are visible to you yet. Create a process first to scope this invitation.
                </p>
              )}
            </div>
          )}

          {scope === "organization" && matrix.inviter_role === "admin" && (
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Target organization <span className="text-rose-500">*</span>
              </label>
              <select
                value={form.target_org_id}
                onChange={(e) => setForm({ ...form, target_org_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                required
                data-testid="role-invite-org-select"
              >
                <option value="">— Choose organization to seed —</option>
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}{o.code ? ` (${o.code})` : ""}</option>
                ))}
              </select>
            </div>
          )}

          {/* Recipient details */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                First name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="role-invite-firstname-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Last name</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="role-invite-lastname-input"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Email <span className="text-rose-500">*</span>
            </label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="invitee@company.com"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="role-invite-email-input"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Hierarchical invite · 7-day expiry
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
                data-testid="role-invite-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !form.role || matrixLoading || matrix.options.length === 0}
                className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-1.5 rounded-md flex items-center gap-2"
                data-testid="role-invite-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {submitting ? "Sending…" : "Send invitation"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
